import { Icon } from '@analog/ui';
import { Tooltip } from '@analog/ui/src';
import allImgPaths from 'assets/allImgPaths';
import { Text, TruncatedText } from 'components';
import Badge from 'components/common/Badge';
import JsonTable from 'components/common/JsonTable';
import { Skeleton } from 'components/Skeleton';
import TableColumns from 'components/Table/TableColumns';
import { FontColors } from 'consts';
import moment from 'moment';
import { tooltipOfPhase } from 'pages/GMP/GMPList/components/GMPMessageColumns';
import { useNavigate } from 'react-router-dom';
import { routes } from 'routes/routesConst';
import
  {
    classes,
    colors,
    colorType,
    DBtoStringValueMapping,
    getIcon,
    getLinkFromNetwork,
    getNetworkName,
    parseNetwork,
    removeZeros,
    timeAgo,
  } from 'utils';

import { GMPMessageDataProps } from './useTaskData';

const GMPMessageData = ({ gmpMessageDetailsData }: GMPMessageDataProps) => {
  const navigate = useNavigate();
  const destNetwork = getNetworkName(
    gmpMessageDetailsData?.GMPMessagesDetails?.destNetwork as string
  );

  return [
    {
      icon: <Icon icon={allImgPaths.taskIcon} />,
      label: 'Message ID',
      value: (
        <TruncatedText
          originalText={gmpMessageDetailsData?.GMPMessagesDetails.messageId as string}
          textColor={FontColors.WHITE}
          withCopyButton
          dataToolTipId={gmpMessageDetailsData?.GMPMessagesDetails.messageId as string}
          dataToolTipContent={gmpMessageDetailsData?.GMPMessagesDetails.messageId as string}
        />
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails.messageId,
    },
    {
      icon: <Icon icon={allImgPaths.hashTagOutlineIcon} />,
      label: 'Message Received Task ID',
      value: (
        <Text
          color={FontColors.PURPLE}
          extraClass="cursor-pointer"
          onClick={() =>
            navigate(
              `${routes.task}/${gmpMessageDetailsData?.GMPMessagesDetails.messageReceivedTaskId}`
            )
          }
        >
          {gmpMessageDetailsData?.GMPMessagesDetails.messageReceivedTaskId}
        </Text>
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails.messageReceivedTaskId || false,
    },
    {
      icon: <Icon icon={allImgPaths.hashTagOutlineIcon} />,
      label: 'Message Executed Task ID',
      value: (
        <Text
          color={FontColors.PURPLE}
          extraClass="cursor-pointer"
          onClick={() =>
            navigate(
              `${routes.task}/${gmpMessageDetailsData?.GMPMessagesDetails.messageExecutedTaskId}`
            )
          }
        >
          {gmpMessageDetailsData?.GMPMessagesDetails.messageExecutedTaskId}
        </Text>
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails.messageExecutedTaskId || false,
    },
    {
      icon: <Icon icon={allImgPaths.taskIcon} />,
      label: 'Assigned Batch ID',
      value: (
        <Text
          color={FontColors.PURPLE}
          extraClass="cursor-pointer"
          onClick={() =>
            navigate(
              `${routes.submitGateway}/${gmpMessageDetailsData?.GMPMessagesDetails.assignedBatchId}`
            )
          }
        >
          {gmpMessageDetailsData?.GMPMessagesDetails.assignedBatchId}
        </Text>
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails.assignedBatchId,
    },
    {
      icon: <Icon icon={allImgPaths.taskIcon} />,
      label: 'Submit Gateway Task ID',
      value: (
        <Text
          color={FontColors.PURPLE}
          extraClass="cursor-pointer"
          onClick={() =>
            navigate(
              `${routes.submitGateway}/${gmpMessageDetailsData?.GMPMessagesDetails.SubmitGatewayEventTaskId}`,
              { state: { isTaskId: true } }
            )
          }
        >
          {gmpMessageDetailsData?.GMPMessagesDetails.SubmitGatewayEventTaskId}
        </Text>
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails.SubmitGatewayEventTaskId,
    },
    {
      icon: <Icon icon={allImgPaths.phaseIcon} />,
      label: 'Status',
      value: (
        <Skeleton.Loader className="w-20 h-5">
          <Tooltip
            id={
              tooltipOfPhase[
                gmpMessageDetailsData?.GMPMessagesDetails?.status?.toUpperCase() as keyof typeof tooltipOfPhase
              ]
            }
            content={
              tooltipOfPhase[
                gmpMessageDetailsData?.GMPMessagesDetails?.status?.toUpperCase() as keyof typeof tooltipOfPhase
              ]
            }
            className="max-w-[220px] md:!max-w-fit !rounded-lg z-10"
            clickable
          />
          <Badge
            label={
              gmpMessageDetailsData?.GMPMessagesDetails?.status
                ? DBtoStringValueMapping[
                    gmpMessageDetailsData?.GMPMessagesDetails?.status.toLowerCase() as keyof typeof DBtoStringValueMapping
                  ] || gmpMessageDetailsData?.GMPMessagesDetails?.status
                : ''
            }
            badgeColor={
              gmpMessageDetailsData?.GMPMessagesDetails?.status &&
              gmpMessageDetailsData?.GMPMessagesDetails?.status.toLowerCase() in colors
                ? (colors[
                    gmpMessageDetailsData?.GMPMessagesDetails?.status.toLowerCase() as keyof typeof colors
                  ] as colorType)
                : ('#808080' as colorType)
            }
            data-tooltip-id={
              tooltipOfPhase[
                gmpMessageDetailsData?.GMPMessagesDetails?.status?.toUpperCase() as keyof typeof tooltipOfPhase
              ]
            }
          />
        </Skeleton.Loader>
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails.status || false,
    },
    {
      icon: <Icon icon={allImgPaths.sourceNetworkIcon} />,
      label: 'Source Network',
      value: (
        <div className="flex gap-1 text-white">
          {gmpMessageDetailsData?.GMPMessagesDetails.srcNetwork ? (
            <Skeleton.Loader>
              <div className={classes('flex gap-1 flex-wrap')}>
                <TableColumns
                  key={gmpMessageDetailsData?.GMPMessagesDetails.srcNetwork}
                  beforeImagePath={
                    gmpMessageDetailsData?.GMPMessagesDetails.srcNetwork?.split('-') &&
                    getIcon(gmpMessageDetailsData?.GMPMessagesDetails.srcNetwork.split('-')[0])
                  }
                  value={gmpMessageDetailsData?.GMPMessagesDetails.srcNetwork}
                  textColor={FontColors.WHITE}
                  classNames={{
                    container: '!w-fit',
                    base: '!w-fit',
                    content: 'capitalize',
                    skeletonText: 'h-4 w-12',
                  }}
                />
              </div>
            </Skeleton.Loader>
          ) : (
            <Skeleton.Loader className="h-4 w-10">-</Skeleton.Loader>
          )}
        </div>
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails.srcNetwork,
    },

    {
      icon: <Icon icon={allImgPaths.shardNetworkIcon} />,
      label: 'Destination Network',
      value: (
        <div className="flex gap-1 text-white flex-wrap">
          {gmpMessageDetailsData?.GMPMessagesDetails.destNetwork ? (
            <Skeleton.Loader>
              <div className={classes('flex gap-1 flex-wrap')}>
                <TableColumns
                  key={gmpMessageDetailsData?.GMPMessagesDetails.destNetwork}
                  beforeImagePath={destNetwork && getIcon(destNetwork)}
                  value={parseNetwork(gmpMessageDetailsData?.GMPMessagesDetails.destNetwork)}
                  textColor={FontColors.WHITE}
                  classNames={{
                    container: '!w-fit',
                    base: '!w-fit',
                    content: 'capitalize',
                    skeletonText: 'h-4 w-12',
                  }}
                />
              </div>
            </Skeleton.Loader>
          ) : (
            <Skeleton.Loader className="h-4 w-10">-</Skeleton.Loader>
          )}
        </div>
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails.destNetwork,
    },

    {
      icon: <Icon icon={allImgPaths.globalSearchIcon} />,
      label: 'Source Address',
      value: (
        <TruncatedText
          originalText={removeZeros(gmpMessageDetailsData?.GMPMessagesDetails.srcAddress as string)}
          textColor={FontColors.PURPLE}
          className="cursor-pointer"
          toolTipClass="!cursor-text"
          onClick={() => {
            const redirectLink = getLinkFromNetwork(
              `address/${removeZeros(
                gmpMessageDetailsData?.GMPMessagesDetails.srcAddress as string
              )}`,
              gmpMessageDetailsData?.GMPMessagesDetails.srcNetwork?.split('-')[0]
            );
            redirectLink && window.open(redirectLink, '_blank');
          }}
          withCopyButton
          dataToolTipId={removeZeros(
            gmpMessageDetailsData?.GMPMessagesDetails.srcAddress as string
          )}
          dataToolTipContent={removeZeros(
            gmpMessageDetailsData?.GMPMessagesDetails.srcAddress as string
          )}
        />
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails.srcAddress,
    },

    {
      icon: <Icon icon={allImgPaths.globalSearchIcon} />,
      label: 'Destination Address',
      value: (
        <TruncatedText
          originalText={gmpMessageDetailsData?.GMPMessagesDetails.destinationAddress as string}
          textColor={FontColors.PURPLE}
          className="cursor-pointer"
          toolTipClass="!cursor-text"
          onClick={() => {
            const redirectLink = getLinkFromNetwork(
              `address/${gmpMessageDetailsData?.GMPMessagesDetails.destinationAddress}`,
              destNetwork
            );
            redirectLink && window.open(redirectLink, '_blank');
          }}
          withCopyButton
          dataToolTipId={gmpMessageDetailsData?.GMPMessagesDetails.destinationAddress as string}
          dataToolTipContent={
            gmpMessageDetailsData?.GMPMessagesDetails.destinationAddress as string
          }
        />
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails.destinationAddress,
    },
    {
      icon: <Icon icon={allImgPaths.hashTagIcon} />,
      label: 'Source Transaction hash',
      value: (
        <TruncatedText
          originalText={gmpMessageDetailsData?.GMPMessagesDetails.srcTransactionHash as string}
          textColor={FontColors.PURPLE}
          className="cursor-pointer"
          toolTipClass="!cursor-text"
          onClick={() => {
            const redirectLink = getLinkFromNetwork(
              `tx/${gmpMessageDetailsData?.GMPMessagesDetails.srcTransactionHash}`,
              gmpMessageDetailsData?.GMPMessagesDetails.srcNetwork?.split('-')[0]
            );
            redirectLink && window.open(redirectLink, '_blank');
          }}
          withCopyButton
          dataToolTipId={gmpMessageDetailsData?.GMPMessagesDetails.srcTransactionHash as string}
          dataToolTipContent={
            gmpMessageDetailsData?.GMPMessagesDetails.srcTransactionHash as string
          }
        />
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails.srcTransactionHash,
    },
    {
      icon: <Icon icon={allImgPaths.hashTagIcon} />,
      label: 'Destination Transaction Hash',
      value: (
        <TruncatedText
          originalText={
            (gmpMessageDetailsData?.GMPMessagesDetails.destTransactionHash as string) ||
            (gmpMessageDetailsData?.GMPMessagesDetails?.SubmitGatewayEventTask
              ?.batchTxHash as string)
          }
          textColor={FontColors.PURPLE}
          className="cursor-pointer"
          toolTipClass="!cursor-text"
          onClick={() => {
            const redirectLink = getLinkFromNetwork(
              `tx/${
                gmpMessageDetailsData?.GMPMessagesDetails.destTransactionHash ||
                (gmpMessageDetailsData?.GMPMessagesDetails?.SubmitGatewayEventTask
                  ?.batchTxHash as string)
              }`,
              destNetwork
            );
            redirectLink && window.open(redirectLink, '_blank');
          }}
          withCopyButton
          dataToolTipId={
            (gmpMessageDetailsData?.GMPMessagesDetails.destTransactionHash as string) ||
            (gmpMessageDetailsData?.GMPMessagesDetails?.SubmitGatewayEventTask
              ?.batchTxHash as string)
          }
          dataToolTipContent={
            (gmpMessageDetailsData?.GMPMessagesDetails.destTransactionHash as string) ||
            (gmpMessageDetailsData?.GMPMessagesDetails?.SubmitGatewayEventTask
              ?.batchTxHash as string)
          }
        />
      ),
      enable: !!(
        gmpMessageDetailsData?.GMPMessagesDetails.destTransactionHash ||
        (gmpMessageDetailsData?.GMPMessagesDetails?.SubmitGatewayEventTask?.batchTxHash as string)
      ),
    },
    {
      icon: <Icon icon={allImgPaths.clockIcon} />,
      label: 'Created At',
      value: gmpMessageDetailsData?.GMPMessagesDetails?.created_at
        ? `${timeAgo(new Date(gmpMessageDetailsData?.GMPMessagesDetails?.created_at))} (${moment
            .utc(gmpMessageDetailsData?.GMPMessagesDetails?.created_at)
            .format('MMM-D-YYYY h:mm:ss A +UTC')})`
        : '-',
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails?.created_at,
    },
    {
      icon: <Icon icon={allImgPaths.clockIcon} />,
      label: 'Updated At',
      value: gmpMessageDetailsData?.GMPMessagesDetails?.updated_at
        ? `${timeAgo(new Date(gmpMessageDetailsData?.GMPMessagesDetails?.updated_at))} (${moment
            .utc(gmpMessageDetailsData?.GMPMessagesDetails?.updated_at)
            .format('MMM-D-YYYY h:mm:ss A +UTC')})`
        : '-',
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails?.updated_at,
    },
    {
      icon: <Icon icon={allImgPaths.clipboardTickIcon} />,
      label: 'Source Block',
      value: (
        <Text
          extraClass="cursor-pointer"
          color={FontColors.PURPLE}
          onClick={() => {
            const redirectLink = getLinkFromNetwork(
              `block/${gmpMessageDetailsData?.GMPMessagesDetails?.srcBlockNumber}`,
              gmpMessageDetailsData?.GMPMessagesDetails?.srcNetwork?.split('-')[0]
            );
            redirectLink && window.open(redirectLink, '_blank');
          }}
        >
          {gmpMessageDetailsData?.GMPMessagesDetails?.srcBlockNumber}
        </Text>
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails?.srcBlockNumber,
    },
    {
      icon: <Icon icon={allImgPaths.clipboardTickIcon} />,
      label: 'Destination Block',
      value: (
        <Text
          extraClass="cursor-pointer"
          color={FontColors.PURPLE}
          onClick={() => {
            const redirectLink = getLinkFromNetwork(
              `block/${gmpMessageDetailsData?.GMPMessagesDetails?.destBlockNumber}`,
              destNetwork
            );
            redirectLink && window.open(redirectLink, '_blank');
          }}
        >
          {gmpMessageDetailsData?.GMPMessagesDetails?.destBlockNumber}
        </Text>
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails?.destBlockNumber,
    },
    {
      icon: <Icon icon={allImgPaths.infoIcon} />,
      label: 'Source Transaction Log',
      collapsible: true,
      value: (
        <div>
          {gmpMessageDetailsData?.GMPMessagesDetails?.srcEventLog && (
            <JsonTable
              index={gmpMessageDetailsData?.GMPMessagesDetails?.srcEventLog.toString() as string}
              jsonData={gmpMessageDetailsData?.GMPMessagesDetails?.srcEventLog}
            />
          )}
        </div>
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails?.srcEventLog,
    },
    {
      icon: <Icon icon={allImgPaths.infoIcon} />,
      label: 'Destination Transaction Log',
      collapsible: true,
      value: (
        <div>
          {gmpMessageDetailsData?.GMPMessagesDetails?.destEventLog && (
            <JsonTable
              index={gmpMessageDetailsData?.GMPMessagesDetails?.destEventLog as string}
              jsonData={gmpMessageDetailsData?.GMPMessagesDetails?.destEventLog}
            />
          )}
        </div>
      ),
      enable: !!gmpMessageDetailsData?.GMPMessagesDetails?.destEventLog,
    },
  ];
};
export default GMPMessageData;
